import { useEffect, useRef, useState } from 'react'
import {
  ChevronDown,
  Clock,
  LogOut,
  Moon,
  ShieldCheck,
  Sun,
  UserCog,
  Users,
  UtensilsCrossed,
} from 'lucide-react'
import type { Theme } from '../hooks/useTheme'
import { useLiveClock } from '../hooks/useLiveClock'
import { formatLongDate, formatWallClock } from '../lib/format'
import { Avatar } from './Avatar'
import { SystemStatusMenu } from './SystemStatusMenu'

export type SettingsTarget = 'account' | 'profile'

/** The pages the app can show (kept in step with `lib/router.ts`). */
export type AppView =
  | 'dashboard'
  | 'users'
  | 'account'
  | 'profile'
  | 'insights'
  | 'fourwall'
  | 'grid'
  | 'sales'
  | 'products'

interface TopNavProps {
  theme: Theme
  onToggleTheme: () => void
  /** Signed-in person, straight from the identity service (§9.7). */
  displayName?: string
  /** Their profile picture, if they set one (D-44) — initials otherwise. */
  avatar?: string | null
  roleLabel?: string
  onSignOut?: () => void
  /** Opens Account/Profile settings from the name menu (§13.8). */
  onOpenSettings?: (target: SettingsTarget) => void
  /** Navigates to one of the account-menu destinations (currently User Management). */
  onNavigate?: (path: string) => void
  /** Shows “User Management” in the account menu — only for roles that may administer (§13.7). */
  showUserManagement?: boolean
}

export function TopNav({
  theme,
  onToggleTheme,
  displayName,
  avatar,
  roleLabel,
  onSignOut,
  onOpenSettings,
  onNavigate,
  showUserManagement = false,
}: TopNavProps) {
  const now = useLiveClock()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-ink-950/80">
      <div className="mx-auto flex w-full max-w-[1600px] items-center gap-2 px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3 lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:px-8">
        {/* Brand / return home ------------------------------------------- */}
        <button
          type="button"
          onClick={() => onNavigate?.('/general-overview')}
          className="flex min-w-0 items-center gap-2.5 text-left sm:gap-3"
          aria-label="Go to the dashboard"
          title="Go to the dashboard"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-salsa-500 to-mango-500 text-white shadow-lg shadow-salsa-500/25 sm:h-10 sm:w-10">
            <UtensilsCrossed className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="min-w-0 leading-tight">
            <span className="flex items-center gap-2">
              <span className="truncate text-sm font-semibold tracking-tight sm:text-base">
                Salsa Shop
              </span>
              <span className="badge hidden bg-salsa-50 text-salsa-700 sm:inline-flex dark:bg-salsa-500/15 dark:text-salsa-300">
                Ops Hub
              </span>
            </span>
            <span className="hidden text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 sm:block dark:text-slate-500">
              Store Operations Platform
            </span>
          </span>
        </button>

        {/* Clock --------------------------------------------------------- */}
        {/* Phones already show the time in the status bar, and the row it used to
            occupy is better spent on the screen itself (D-106). */}
        <div className="hidden flex-wrap items-center justify-center gap-2 sm:flex md:order-none md:mx-auto md:w-auto lg:mx-0 lg:w-auto">
          <span className="pill font-mono tabular-nums">
            <Clock className="h-4 w-4 text-slate-400" aria-hidden="true" />
            {formatWallClock(now)}
          </span>
          <span className="hidden text-xs font-medium text-slate-500 lg:inline dark:text-slate-400">
            {formatLongDate(now)}
          </span>
        </div>

        {/* Actions + profile -------------------------------------------- */}
        <div className="ml-auto flex items-center gap-2 lg:ml-0 lg:justify-self-end">
          <SystemStatusMenu />

          <button
            type="button"
            onClick={onToggleTheme}
            className="icon-button"
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Moon className="h-4 w-4" aria-hidden="true" />
            )}
          </button>

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              className="flex items-center gap-2.5 rounded-2xl border border-slate-200 bg-white py-1.5 pl-1.5 pr-3 text-left transition-colors hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
            >
              <Avatar name={displayName ?? 'Alex B.'} avatar={avatar} />
              <span className="hidden leading-tight sm:block">
                <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {displayName ?? 'Alex B.'}
                </span>
                <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                  {roleLabel ?? 'General Manager'}
                </span>
              </span>
              <ChevronDown
                className={`h-3.5 w-3.5 text-slate-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>

            {menuOpen && (
              <div
                role="menu"
                aria-label="Account menu"
                className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1 shadow-xl dark:border-white/10 dark:bg-ink-900"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false)
                    onOpenSettings?.('account')
                  }}
                  className="flex w-full items-start gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/5"
                >
                  <ShieldCheck className="mt-0.5 h-4 w-4 text-salsa-500" aria-hidden="true" />
                  <span className="leading-tight">
                    <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                      Account Settings
                    </span>
                    <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                      Password, e-mail address, two-factor
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false)
                    onOpenSettings?.('profile')
                  }}
                  className="flex w-full items-start gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/5"
                >
                  <UserCog className="mt-0.5 h-4 w-4 text-salsa-500" aria-hidden="true" />
                  <span className="leading-tight">
                    <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                      Profile Settings
                    </span>
                    <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                      Your name, language and notifications
                    </span>
                  </span>
                </button>

                {showUserManagement && onNavigate && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false)
                      onNavigate('/users')
                    }}
                    className="flex w-full items-start gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/5"
                  >
                    <Users className="mt-0.5 h-4 w-4 text-salsa-500" aria-hidden="true" />
                    <span className="leading-tight">
                      <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                        User Management
                      </span>
                      <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                        Accounts and access in your scope
                      </span>
                    </span>
                  </button>
                )}

                {onSignOut && (
                  <>
                    <div className="my-1 border-t border-slate-100 dark:border-white/5" />
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false)
                        onSignOut()
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium text-salsa-600 transition-colors hover:bg-salsa-500/10 dark:text-salsa-300"
                    >
                      <LogOut className="h-4 w-4" aria-hidden="true" />
                      Sign out
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
